import React, { useState, useEffect, useCallback, useMemo } from 'react';

import axios from 'axios';

import { styled } from '@mui/material/styles';

import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    TextField,
    Typography,
    Grid,
    IconButton,
    Alert,
    Input,
    CircularProgress,
    Autocomplete,
    DialogContentText,
    Snackbar,
    FormGroup,
    Checkbox,
    Box,
} from '@mui/material';

import EditIcon from '@mui/icons-material/Edit';

import DeleteIcon from '@mui/icons-material/Delete';

import AddIcon from '@mui/icons-material/Add';
import ImageIcon from '@mui/icons-material/Image';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';

import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { useTest } from '../components/TestContext';
import debounce from 'lodash/debounce';
import MenuItem from '@mui/material/MenuItem';
import ImageUploadDialog from './ImageUploadDialog';
import PurchaseOptionsManager from '../components/PurchaseOptionsManager';
import SettingsIcon from '@mui/icons-material/Settings';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ScaleIcon from '@mui/icons-material/Scale';
import StraightenIcon from '@mui/icons-material/Straighten';
import InputAdornment from '@mui/material/InputAdornment';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    fontWeight: 'bold',
}));

const Products = () => {
    const { tests, loading: testsLoading, error: testsError, refreshTests } = useTest();

    const [products, setProducts] = useState([]);

    const [newProduct, setNewProduct] = useState({
        name: '',
        price: '',
        description: '',
        imageUrl: '',
        allowedTest: null,
        allowedTests: [],
        isHidden: false,
        state: ['Louisiana'], // Change from string to array
        type: 'Original Practice Test',
    });
    const [editingProduct, setEditingProduct] = useState(null);

    const [error, setError] = useState('');

    const [imageFile, setImageFile] = useState(null);

    const [loading, setLoading] = useState(true);

    //const [setTests] = useState([]);

    //const [testsPage, setTestsPage] = useState(1);

    //const [hasMoreTests, setHasMoreTests] = useState(true);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [productToDelete, setProductToDelete] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const STATES = ['Louisiana', 'Mississippi', 'South Carolina', 'Tennessee', 'NASCLA'];
    const PRODUCT_TYPES = ['Original Practice Test', 'Previously Used Exam Questions'];
    const [stateFilter, setStateFilter] = useState([]);
    const [typeFilter, setTypeFilter] = useState('all');
    const [imageUploadDialogOpen, setImageUploadDialogOpen] = useState(false);
    const [activeImageUpload, setActiveImageUpload] = useState('new'); // 'new' or 'edit'
    const [purchaseOptionsDialogOpen, setPurchaseOptionsDialogOpen] = useState(false);
    const [selectedProductForOptions, setSelectedProductForOptions] = useState(null);

    const getImageUrl = (path) => {
        if (!path) return '/api/placeholder/400/300';

        // If it's already a full URL, return it as is
        if (path.startsWith('http://') || path.startsWith('https://')) {
            return path;
        }

        // Clean the path by removing any leading slashes
        const cleanPath = path.replace(/^\/+/, '');

        // If the path already includes 'uploads/', don't add it again
        if (cleanPath.startsWith('uploads/')) {
            return `${process.env.REACT_APP_API_URL}/${cleanPath}`;
        }

        // Otherwise, ensure uploads/ is in the path
        return `${process.env.REACT_APP_API_URL}/uploads/${cleanPath}`;
    };
    const filteredTests = useMemo(() => {
        if (!searchInput.trim()) return tests;
        return tests.filter(
            (test) =>
                test.name.toLowerCase().includes(searchInput.toLowerCase()) ||
                test.category?.toLowerCase().includes(searchInput.toLowerCase()),
        );
    }, [tests, searchInput]);

    const handleRefresh = useCallback(async () => {
        if (isSearching) return; // Prevent refresh while searching
        console.log('Starting products refresh...');
        setLoading(true);
        try {
            await refreshTests();
            await fetchProducts();
        } catch (error) {
            console.error('Refresh failed:', error);
            setError('Failed to refresh data');
        } finally {
            setLoading(false);
        }
    }, [refreshTests, isSearching]);

    const debouncedSearch = useCallback(
        debounce((inputValue) => {
            setSearchInput(inputValue);
            setIsSearching(false);
        }, 300),
        [],
    );

    const StateSelection = ({ selectedStates, onChange }) => {
        const handleStateChange = (state) => (event) => {
            if (event.target.checked) {
                onChange([...selectedStates, state]);
            } else {
                onChange(selectedStates.filter((s) => s !== state));
            }
        };

        return (
            <FormGroup>
                {STATES.map((state) => (
                    <FormControlLabel
                        key={state}
                        control={<Checkbox checked={selectedStates.includes(state)} onChange={handleStateChange(state)} />}
                        label={state}
                    />
                ))}
            </FormGroup>
        );
    };

    const filteredProducts = useMemo(() => {
        return products.filter((product) => {
            const matchesState = stateFilter.length === 0 || product.state.some((state) => stateFilter.includes(state));
            const matchesType = typeFilter === 'all' || product.type === typeFilter;
            return matchesState && matchesType;
        });
    }, [products, stateFilter, typeFilter]);

    const findTestById = (testId) => {
        return tests.find((test) => test._id === testId) || null;
    };

    // useEffect(() => {
    //     // Fetch products and refresh tests when component mounts
    //     fetchProducts();
    //     refreshTests();
    // }, [refreshTests]);

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            try {
                await Promise.all([fetchProducts(), refreshTests()]);
            } catch (error) {
                console.error('Initialization failed:', error);
                setError('Failed to initialize data');
            } finally {
                setLoading(false);
            }
        };
        init();
    }, [refreshTests]);

    const deleteProduct = async (productId) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No authentication token found. Please log in.');
            }

            await axios.delete(`${process.env.REACT_APP_API_URL}/api/products/${productId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            // Remove the deleted product from the state
            setProducts(products.filter((product) => product._id !== productId));
        } catch (error) {
            console.error('Error deleting product:', error);
            setError(`Error deleting product: ${error.message}`);
        }
    };

    const handleDeleteClick = (product) => {
        setProductToDelete(product);
        setDeleteConfirmOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (productToDelete) {
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    throw new Error('No authentication token found. Please log in.');
                }

                await axios.delete(`${process.env.REACT_APP_API_URL}/api/products/${productToDelete._id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                // Remove the deleted product from the state
                setProducts(products.filter((product) => product._id !== productToDelete._id));
                setSuccessMessage('Product has been successfully deleted');
                setDeleteConfirmOpen(false);
                setProductToDelete(null);
            } catch (error) {
                console.error('Error deleting product:', error);
                setError(`Error deleting product: ${error.message}`);
            }
        }
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmOpen(false);
        setProductToDelete(null);
    };

    const handleSuccessClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSuccessMessage('');
    };

    const handleOpenEditDialog = (product) => {
        refreshTests(); // Refresh tests before opening dialog
        setEditingProduct({
            ...product,
            allowedTest: product.allowedTest
                ? {
                      _id: product.allowedTest._id,
                      name: product.allowedTest.name,
                  }
                : null,
            allowedTests: product.allowedTests || (product.allowedTest ? [product.allowedTest._id || product.allowedTest] : []),
            bookPackageInfo: product.bookPackageInfo || { 
                cost: 0, 
                weight: 0,
                dimensions: {
                    length: 12,
                    width: 12,
                    height: 12
                }
            }
        });
        setOpenEditDialog(true);
    };

    // Removing standalone Autocomplete component - it needs to be inside a function component.

    const fetchProducts = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/products`);
            if (response.data?.status === 'success' && Array.isArray(response.data.data.data)) {
                const productsWithTests = response.data.data.data.map((product) => ({
                    ...product,
                    allowedTest: product.allowedTest
                        ? {
                              _id: product.allowedTest._id,
                              name: product.allowedTest.name,
                          }
                        : null,
                }));
                setProducts(productsWithTests);
            } else {
                throw new Error('Invalid response format');
            }
        } catch (error) {
            console.error('Error fetching products:', error);
            setError(`Error fetching products: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const addProduct = async () => {
        try {
            if (!newProduct.imageUrl) {
                throw new Error('Please upload an image');
            }

            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/products`, newProduct, {
                headers: { Authorization: `Bearer ${token}` },
            });

            await fetchProducts();
            setNewProduct({
                name: '',
                price: '',
                description: '',
                imageUrl: '',
                allowedTest: null,
                allowedTests: [],
                isHidden: false,
                state: ['Louisiana'], // Add this line
                type: 'Original Practice Test', // Add this line
            });
            setSuccessMessage('Product added successfully');
        } catch (error) {
            setError(error.message);
        }
    };

    const updateProduct = async () => {
        try {
            if (!editingProduct?._id) throw new Error('No product selected for editing');

            const token = localStorage.getItem('token');
            if (!token) throw new Error('No authentication token found');

            // Ensure allowedTest is either null or an ObjectId
            const allowedTestId = editingProduct.allowedTest
                ? typeof editingProduct.allowedTest === 'object'
                    ? editingProduct.allowedTest._id
                    : editingProduct.allowedTest
                : null;

            // Create the update payload
            const updatePayload = {
                ...editingProduct,
                allowedTest: allowedTestId, // Use the extracted ID
                state: Array.isArray(editingProduct.state) ? editingProduct.state : [editingProduct.state],
            };

            const response = await axios.put(`${process.env.REACT_APP_API_URL}/api/products/${editingProduct._id}`, updatePayload, {
                headers: { Authorization: `Bearer ${token}` },
            });

            await fetchProducts();
            setEditingProduct(null);
            setOpenEditDialog(false);
            setSuccessMessage('Product updated successfully');
        } catch (error) {
            console.error('Update error details:', {
                message: error.message,
                response: error.response?.data,
            });
            setError(error.response?.data?.message || error.message);
        }
    };

    const loadMoreTests = () => {
        if (hasMoreTests) {
            fetchTests(testsPage + 1);
        }
    };

    const handleSavePurchaseOptions = async (options, shippingInfo) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) throw new Error('No authentication token found');

            // Update the product with both purchase options and shipping info
            const updatedProduct = {
                ...selectedProductForOptions,
                purchaseOptions: options,
                bookPackageInfo: {
                    ...selectedProductForOptions.bookPackageInfo,
                    weight: shippingInfo.weight,
                    dimensions: shippingInfo.dimensions
                }
            };

            const response = await axios.put(
                `${process.env.REACT_APP_API_URL}/api/products/${selectedProductForOptions._id}`,
                updatedProduct,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            await fetchProducts();
            setSuccessMessage('Purchase options updated successfully');
            setPurchaseOptionsDialogOpen(false);
            setSelectedProductForOptions(null);
        } catch (error) {
            console.error('Error updating purchase options:', error);
            setError(error.response?.data?.message || error.message);
        }
    };

    const uploadImage = async (file) => {
        if (!file) {
            throw new Error('No image file provided');
        }

        const formData = new FormData();
        formData.append('image', file);

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/upload/upload-image`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.data?.url) {
                throw new Error('Invalid image upload response');
            }

            return response.data.url;
        } catch (error) {
            console.error('Image upload error:', error);
            throw new Error(error.response?.data?.message || 'Error uploading image');
        }
    };

    const handleCloseEditDialog = () => {
        setEditingProduct(null);

        setOpenEditDialog(false);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setNewProduct({ ...newProduct, [name]: value });
    };

    const handleEditInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'state') {
            setEditingProduct((prev) => ({
                ...prev,
                [name]: Array.isArray(value) ? value : [value],
            }));
        } else {
            setEditingProduct((prev) => ({
                ...prev,
                [name]: value,
            }));
        }
    };

    const handleEditAllowedTestChange = (event, newValue) => {
        setEditingProduct((prev) => ({
            ...prev,
            allowedTest: newValue
                ? {
                      _id: newValue._id,
                      name: newValue.name,
                  }
                : null,
        }));
    };

    const renderAutocomplete = (value, onChange, label = 'Allowed Test') => {
        return (
            <Autocomplete
                options={Array.isArray(tests) ? tests : []}
                getOptionLabel={(option) => {
                    if (!option) return '';
                    if (typeof option === 'string') return option;
                    return option.name || option.category || '';
                }}
                renderInput={(params) => <TextField {...params} label={label} error={!value && testsError} helperText={testsError} />}
                value={value}
                onChange={(event, newValue) => {
                    onChange(event, newValue);
                }}
                isOptionEqualToValue={(option, value) => {
                    if (!option || !value) return false;
                    return option._id === (value._id || value);
                }}
                loading={testsLoading}
            />
        );
    };

    return (
        <div style={{ padding: '20px' }}>
            <Typography variant="h4" gutterBottom>
                Manage Products
            </Typography>

            {error && (
                <Alert severity="error" style={{ marginBottom: '20px' }}>
                    {error}
                </Alert>
            )}

            <Paper style={{ padding: '20px', marginBottom: '20px' }}>
                <Typography variant="h5" gutterBottom>
                    Add New Product
                </Typography>

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Product Name"
                            name="name"
                            value={newProduct.name}
                            onChange={(e) =>
                                setNewProduct((prev) => ({
                                    ...prev,
                                    name: e.target.value,
                                }))
                            }
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Price"
                            name="price"
                            type="number"
                            value={newProduct.price}
                            onChange={(e) =>
                                setNewProduct((prev) => ({
                                    ...prev,
                                    price: e.target.value,
                                }))
                            }
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Description"
                            name="description"
                            value={newProduct.description}
                            onChange={(e) =>
                                setNewProduct((prev) => ({
                                    ...prev,
                                    description: e.target.value,
                                }))
                            }
                            multiline
                            rows={4}
                        />
                    </Grid>

                    {/* <Grid item xs={12}>
                        {renderAutocomplete(
                            editingProduct?.allowedTest || null,
                            (event, newValue) => {
                                setEditingProduct((prev) => ({
                                    ...prev,
                                    allowedTest: newValue ? newValue._id : null,
                                }));
                            },
                        )}
                    </Grid> */}

                    <Grid item xs={12}>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                            Allowed Tests (Maximum 2)
                        </Typography>
                        <Autocomplete
                            multiple
                            options={Array.isArray(tests) ? tests : []}
                            getOptionLabel={(option) => {
                                if (!option) return '';
                                if (typeof option === 'string') {
                                    const test = tests.find(t => t._id === option);
                                    return test ? test.name : option;
                                }
                                return option.name || option.category || '';
                            }}
                            renderInput={(params) => (
                                <TextField 
                                    {...params} 
                                    label="Select up to 2 tests" 
                                    error={newProduct.allowedTests.length > 2}
                                    helperText={newProduct.allowedTests.length > 2 ? "Maximum 2 tests allowed" : ""}
                                />
                            )}
                            value={newProduct.allowedTests.map(id => tests.find(t => t._id === id) || id)}
                            onChange={(event, newValue) => {
                                const testIds = newValue.slice(0, 2).map(test => 
                                    typeof test === 'string' ? test : test._id
                                );
                                setNewProduct((prevState) => ({
                                    ...prevState,
                                    allowedTests: testIds,
                                    allowedTest: testIds[0] || null
                                }));
                            }}
                            isOptionEqualToValue={(option, value) => {
                                if (!option || !value) return false;
                                const optionId = typeof option === 'string' ? option : option._id;
                                const valueId = typeof value === 'string' ? value : value._id;
                                return optionId === valueId;
                            }}
                            loading={testsLoading}
                            limitTags={2}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="subtitle1" sx={{ mt: 2 }}>
                            States
                        </Typography>
                        <StateSelection
                            selectedStates={newProduct.state || []} // Add fallback array
                            onChange={(states) =>
                                setNewProduct((prev) => ({
                                    ...prev,
                                    state: states,
                                }))
                            }
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            select
                            fullWidth
                            label="Type"
                            name="type"
                            value={newProduct.type}
                            onChange={(e) =>
                                setNewProduct((prev) => ({
                                    ...prev,
                                    type: e.target.value,
                                }))
                            }
                        >
                            {PRODUCT_TYPES.map((type) => (
                                <MenuItem key={type} value={type}>
                                    {type}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={newProduct.isHidden}
                                    onChange={(e) =>
                                        setNewProduct((prev) => ({
                                            ...prev,
                                            isHidden: e.target.checked,
                                        }))
                                    }
                                    name="isHidden"
                                    color="primary"
                                />
                            }
                            label="Hide from public view"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Button
                            variant="contained"
                            component="span"
                            startIcon={<ImageIcon />}
                            onClick={() => {
                                setActiveImageUpload('new');
                                setImageUploadDialogOpen(true);
                            }}
                        >
                            Upload Image
                        </Button>
                        {newProduct.imageUrl && (
                            <img
                                src={getImageUrl(newProduct.imageUrl)}
                                alt="Product"
                                style={{
                                    width: '100px',
                                    height: '100px',
                                    objectFit: 'cover',
                                    marginLeft: '10px',
                                }}
                            />
                        )}
                    </Grid>

                    <Grid item xs={12}>
                        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={addProduct}>
                            Add Product
                        </Button>
                    </Grid>
                </Grid>
            </Paper>
            <Paper>
                <Typography variant="h5" gutterBottom style={{ padding: '20px 20px 0' }}>
                    Product List
                </Typography>

                <Paper sx={{ p: 2, mb: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={4}>
                            <Typography variant="subtitle1" sx={{ mt: 2 }}>
                                States
                            </Typography>
                            <StateSelection
                                selectedStates={editingProduct?.state || []}
                                onChange={(states) =>
                                    setEditingProduct((prev) => ({
                                        ...prev,
                                        state: states,
                                    }))
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                select
                                fullWidth
                                label="Filter by Type"
                                value={typeFilter}
                                onChange={(e) => setTypeFilter(e.target.value)}
                            >
                                <MenuItem value="all">All Types</MenuItem>
                                {PRODUCT_TYPES.map((type) => (
                                    <MenuItem key={type} value={type}>
                                        {type}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="body2" color="textSecondary">
                                Showing {filteredProducts.length} of {products.length} products
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>

                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>Name</StyledTableCell>
                                <StyledTableCell>Price</StyledTableCell>
                                <StyledTableCell>Description</StyledTableCell>
                                <StyledTableCell>State</StyledTableCell>
                                <StyledTableCell>Type</StyledTableCell>
                                <StyledTableCell>Allowed Test</StyledTableCell>
                                <StyledTableCell>Image</StyledTableCell>
                                <StyledTableCell>Purchase Options</StyledTableCell>
                                <StyledTableCell>Actions</StyledTableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={12} align="center">
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : filteredProducts.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={12} align="center">
                                        No products found matching the selected filters.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredProducts.map((product) => (
                                    <TableRow key={product._id}>
                                        <TableCell>{product.name}</TableCell>
                                        <TableCell>${product.price}</TableCell>
                                        <TableCell>{product.description}</TableCell>
                                        <TableCell>{product.state.join(', ')}</TableCell>
                                        <TableCell>{product.type}</TableCell>
                                        <TableCell>
                                            {product.allowedTests && product.allowedTests.length > 0 
                                                ? product.allowedTests.map((test, index) => {
                                                    const testObj = typeof test === 'string' 
                                                        ? tests.find(t => t._id === test) 
                                                        : test;
                                                    return (
                                                        <div key={index}>
                                                            {testObj?.name || 'Unknown Test'}
                                                        </div>
                                                    );
                                                  })
                                                : product.allowedTest 
                                                    ? product.allowedTest.name 
                                                    : 'N/A'
                                            }
                                        </TableCell>
                                        <TableCell>
                                            {product.imageUrl && (
                                                <img
                                                    src={getImageUrl(product.imageUrl)}
                                                    alt={product.name}
                                                    style={{
                                                        width: '50px',
                                                        height: '50px',
                                                        objectFit: 'cover',
                                                    }}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <IconButton
                                                color="primary"
                                                onClick={() => {
                                                    setSelectedProductForOptions(product);
                                                    setPurchaseOptionsDialogOpen(true);
                                                }}
                                                title="Manage Purchase Options"
                                            >
                                                <SettingsIcon />
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>
                                            <IconButton
                                                color="primary"
                                                onClick={() => handleOpenEditDialog(product)}
                                                style={{ marginRight: '10px' }}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton color="secondary" onClick={() => handleDeleteClick(product)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>

                        <Dialog open={openEditDialog} onClose={handleCloseEditDialog}>
                            <DialogTitle>Edit Product</DialogTitle>
                            <DialogContent>
                                <TextField
                                    autoFocus
                                    margin="dense"
                                    name="name"
                                    label="Product Name"
                                    type="text"
                                    fullWidth
                                    value={editingProduct?.name || ''}
                                    onChange={handleEditInputChange}
                                />
                                <TextField
                                    margin="dense"
                                    name="price"
                                    label="Price"
                                    type="number"
                                    fullWidth
                                    value={editingProduct?.price || ''}
                                    onChange={handleEditInputChange}
                                />
                                <TextField
                                    margin="dense"
                                    name="description"
                                    label="Description"
                                    type="text"
                                    fullWidth
                                    value={editingProduct?.description || ''}
                                    onChange={handleEditInputChange}
                                />
                                <Grid item xs={12}>
                                    <Button
                                        variant="contained"
                                        component="span"
                                        startIcon={<ImageIcon />}
                                        onClick={() => {
                                            setActiveImageUpload('edit');
                                            setImageUploadDialogOpen(true);
                                        }}
                                    >
                                        Change Image
                                    </Button>
                                    {editingProduct?.imageUrl && (
                                        <img
                                            src={getImageUrl(editingProduct.imageUrl)}
                                            alt="Product"
                                            style={{
                                                width: '100px',
                                                height: '100px',
                                                objectFit: 'cover',
                                                marginLeft: '10px',
                                            }}
                                        />
                                    )}
                                </Grid>
                                <Typography variant="subtitle1" sx={{ mt: 2 }}>
                                    States
                                </Typography>
                                <StateSelection
                                    selectedStates={Array.isArray(editingProduct?.state) ? editingProduct.state : []} // Ensure array
                                    onChange={(states) =>
                                        setEditingProduct((prev) => ({
                                            ...prev,
                                            state: states,
                                        }))
                                    }
                                />

                                <TextField
                                    select
                                    margin="dense"
                                    name="type"
                                    label="Type"
                                    fullWidth
                                    value={editingProduct?.type || 'Original Practice Test'}
                                    onChange={handleEditInputChange}
                                >
                                    {PRODUCT_TYPES.map((type) => (
                                        <MenuItem key={type} value={type}>
                                            {type}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                                    Allowed Tests (Maximum 2)
                                </Typography>
                                <Autocomplete
                                    multiple
                                    options={Array.isArray(tests) ? tests : []}
                                    getOptionLabel={(option) => {
                                        if (!option) return '';
                                        if (typeof option === 'string') {
                                            const test = tests.find(t => t._id === option);
                                            return test ? test.name : option;
                                        }
                                        return option.name || option.category || '';
                                    }}
                                    renderInput={(params) => (
                                        <TextField 
                                            {...params} 
                                            label="Select up to 2 tests" 
                                            error={editingProduct?.allowedTests?.length > 2}
                                            helperText={editingProduct?.allowedTests?.length > 2 ? "Maximum 2 tests allowed" : ""}
                                        />
                                    )}
                                    value={editingProduct?.allowedTests?.map(id => {
                                        if (typeof id === 'string') {
                                            return tests.find(t => t._id === id) || id;
                                        }
                                        return id;
                                    }) || []}
                                    onChange={(event, newValue) => {
                                        const testIds = newValue.slice(0, 2).map(test => 
                                            typeof test === 'string' ? test : test._id
                                        );
                                        setEditingProduct((prev) => ({
                                            ...prev,
                                            allowedTests: testIds,
                                            allowedTest: testIds[0] ? {
                                                _id: testIds[0],
                                                name: tests.find(t => t._id === testIds[0])?.name
                                            } : null
                                        }));
                                    }}
                                    isOptionEqualToValue={(option, value) => {
                                        if (!option || !value) return false;
                                        const optionId = typeof option === 'string' ? option : option._id;
                                        const valueId = typeof value === 'string' ? value : value._id;
                                        return optionId === valueId;
                                    }}
                                    loading={testsLoading}
                                    limitTags={2}
                                />
                                
                                {/* Shipping Information Section */}
                                <Paper elevation={0} sx={{ 
                                    mt: 3, 
                                    p: 2, 
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: 2,
                                    border: '1px solid #e0e0e0'
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <LocalShippingIcon sx={{ mr: 1, color: '#1976d2' }} />
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            Shipping Information
                                        </Typography>
                                    </Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                        These details apply to all book packages (Books Only & Complete Package)
                                    </Typography>
                                    
                                    <Grid container spacing={2}>
                                        {/* Weight */}
                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                fullWidth
                                                label="Package Weight"
                                                type="number"
                                                value={editingProduct?.bookPackageInfo?.weight || ''}
                                                onChange={(e) => {
                                                    setEditingProduct((prev) => ({
                                                        ...prev,
                                                        bookPackageInfo: {
                                                            ...prev.bookPackageInfo,
                                                            weight: parseFloat(e.target.value) || 0
                                                        }
                                                    }));
                                                }}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <ScaleIcon sx={{ color: '#757575', fontSize: 20 }} />
                                                        </InputAdornment>
                                                    ),
                                                    endAdornment: <InputAdornment position="end">lbs</InputAdornment>,
                                                }}
                                                helperText="Weight in pounds"
                                            />
                                        </Grid>
                                        
                                        {/* Dimensions */}
                                        <Grid item xs={12} md={8}>
                                            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                                                <StraightenIcon sx={{ mr: 1, fontSize: 20, color: '#757575' }} />
                                                Box Dimensions (inches)
                                            </Typography>
                                            <Grid container spacing={1}>
                                                <Grid item xs={4}>
                                                    <TextField
                                                        fullWidth
                                                        label="Length"
                                                        type="number"
                                                        size="small"
                                                        value={editingProduct?.bookPackageInfo?.dimensions?.length || ''}
                                                        onChange={(e) => {
                                                            setEditingProduct((prev) => ({
                                                                ...prev,
                                                                bookPackageInfo: {
                                                                    ...prev.bookPackageInfo,
                                                                    dimensions: {
                                                                        ...prev.bookPackageInfo?.dimensions,
                                                                        length: parseFloat(e.target.value) || 0
                                                                    }
                                                                }
                                                            }));
                                                        }}
                                                        InputProps={{
                                                            endAdornment: <InputAdornment position="end">″</InputAdornment>,
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <TextField
                                                        fullWidth
                                                        label="Width"
                                                        type="number"
                                                        size="small"
                                                        value={editingProduct?.bookPackageInfo?.dimensions?.width || ''}
                                                        onChange={(e) => {
                                                            setEditingProduct((prev) => ({
                                                                ...prev,
                                                                bookPackageInfo: {
                                                                    ...prev.bookPackageInfo,
                                                                    dimensions: {
                                                                        ...prev.bookPackageInfo?.dimensions,
                                                                        width: parseFloat(e.target.value) || 0
                                                                    }
                                                                }
                                                            }));
                                                        }}
                                                        InputProps={{
                                                            endAdornment: <InputAdornment position="end">″</InputAdornment>,
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <TextField
                                                        fullWidth
                                                        label="Height"
                                                        type="number"
                                                        size="small"
                                                        value={editingProduct?.bookPackageInfo?.dimensions?.height || ''}
                                                        onChange={(e) => {
                                                            setEditingProduct((prev) => ({
                                                                ...prev,
                                                                bookPackageInfo: {
                                                                    ...prev.bookPackageInfo,
                                                                    dimensions: {
                                                                        ...prev.bookPackageInfo?.dimensions,
                                                                        height: parseFloat(e.target.value) || 0
                                                                    }
                                                                }
                                                            }));
                                                        }}
                                                        InputProps={{
                                                            endAdornment: <InputAdornment position="end">″</InputAdornment>,
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                                L × W × H format for shipping calculations
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Paper>

                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={editingProduct?.isHidden || false}
                                            onChange={(e) =>
                                                setEditingProduct((prev) => ({
                                                    ...prev,
                                                    isHidden: e.target.checked,
                                                }))
                                            }
                                            name="isHidden"
                                            color="primary"
                                        />
                                    }
                                    label="Hide from public view"
                                    sx={{ mt: 2 }}
                                />
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={handleCloseEditDialog}>Cancel</Button>
                                <Button onClick={updateProduct}>Update Product</Button>
                            </DialogActions>
                        </Dialog>
                    </Table>
                </TableContainer>
            </Paper>
            {editingProduct && (
                <Paper style={{ padding: '20px', marginTop: '20px' }}>
                    <Typography variant="h5" gutterBottom>
                        Edit Product
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Product Name"
                                name="name"
                                value={editingProduct.name || ''}
                                onChange={handleEditInputChange}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Price"
                                name="price"
                                type="number"
                                value={editingProduct.price || ''}
                                onChange={handleEditInputChange}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Description"
                                name="description"
                                value={editingProduct.description || ''}
                                onChange={handleEditInputChange}
                                multiline
                                rows={4}
                            />
                        </Grid>


                        <Grid item xs={12}>
                            {renderAutocomplete(editingProduct?.allowedTest, (event, newValue) => {
                                setEditingProduct((prev) => ({
                                    ...prev,
                                    allowedTest: newValue ? newValue._id : null,
                                }));
                            })}
                        </Grid>

                        <Grid item xs={12}>
                            <Button
                                variant="contained"
                                component="span"
                                startIcon={<ImageIcon />}
                                onClick={() => {
                                    setActiveImageUpload('edit');
                                    setImageUploadDialogOpen(true);
                                }}
                            >
                                Change Image
                            </Button>
                            {editingProduct?.imageUrl && (
                                <img
                                    src={getImageUrl(editingProduct.imageUrl)}
                                    alt="Product"
                                    style={{
                                        width: '100px',
                                        height: '100px',
                                        objectFit: 'cover',
                                        marginLeft: '10px',
                                    }}
                                />
                            )}
                        </Grid>

                        <Grid item xs={12}>
                            <Button variant="contained" color="primary" onClick={updateProduct}>
                                Update Product
                            </Button>

                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={() => setEditingProduct(null)}
                                style={{ marginLeft: '10px' }}
                            >
                                Cancel
                            </Button>
                        </Grid>
                    </Grid>

                    <Grid item>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setStateFilter('all');
                                setTypeFilter('all');
                            }}
                            size="small"
                        >
                            Reset Filters
                        </Button>
                    </Grid>
                </Paper>
            )}
            <Dialog
                open={deleteConfirmOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">{'Confirm Delete'}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">Are you sure you want to delete this product?</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteConfirm} color="secondary" autoFocus>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Success Message Snackbar */}
            <Snackbar
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                open={!!successMessage}
                autoHideDuration={6000}
                onClose={handleSuccessClose}
                message={successMessage}
            />

            <ImageUploadDialog
                open={imageUploadDialogOpen}
                onClose={() => setImageUploadDialogOpen(false)}
                currentImage={activeImageUpload === 'new' ? newProduct.imageUrl : editingProduct?.imageUrl}
                onImageSelect={(imageUrl) => {
                    if (activeImageUpload === 'new') {
                        setNewProduct((prev) => ({
                            ...prev,
                            imageUrl,
                        }));
                    } else {
                        setEditingProduct((prev) => ({
                            ...prev,
                            imageUrl,
                        }));
                    }
                }}
            />

            <PurchaseOptionsManager
                open={purchaseOptionsDialogOpen}
                onClose={() => {
                    setPurchaseOptionsDialogOpen(false);
                    setSelectedProductForOptions(null);
                }}
                product={selectedProductForOptions}
                onSave={handleSavePurchaseOptions}
            />
        </div>
    );
};

export default Products;
